import { useTranslation } from 'react-i18next';
import { differenceInDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { FeedItem } from 'types/feeds';
import { useLocalizedDate } from 'utils/useLocalizedDate';
import styles from './FeedItemCard.module.scss';
import { ActivityStateIcon, ActivityStateIconVariant } from './ActivityStateIcon';

interface ActivityDateChipProps {
  status: FeedItem['status'];
  startTime: FeedItem['startTime'];
  endTime: FeedItem['endTime'];
}

// Below this many days remaining the chip switches from the neutral closing date to the red
// countdown, matching the design's "Closing Soon" state.
const CLOSING_SOON_DAYS = 7;

// A close date further out than this carries no urgency for the resident, so the "Closes <date>"
// label and its clock icon are hidden entirely rather than adding noise beside the Open badge.
// This is the single source for the threshold across grid and list views, which both render here.
const CLOSE_DATE_VISIBLE_DAYS = 30;

interface ChipContent {
  label: string;
  icon: ActivityStateIconVariant | null;
}

// Only a running activity carries the filled chip; upcoming and finished ones state their date
// as plain text, so the emphasis stays on the activity residents can still act on.

/**
 * The activity's key date, shown at the top of the card beside the status badge. Which date
 * matters depends on the status: an upcoming activity is defined by when it opens, an open one
 * by when it closes (or how little time is left), and a closed one simply by being done.
 */
export function ActivityDateChip(props: ActivityDateChipProps) {
  const { status, startTime, endTime } = props;
  const { t } = useTranslation();
  const formattedEndTime = useLocalizedDate(endTime!, 'MMM d, yyyy');
  const formattedStartTime = useLocalizedDate(startTime!, 'MMM d, yyyy');

  // The day count depends on the current time, so a cached server render could serve a stale
  // number. Recomputing on mount keeps the countdown honest without changing which chip shows.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
  }, []);

  const getChipContent = (): ChipContent | null => {
    if (status === 'closed') {
      if (!formattedEndTime) return null;
      return { label: formattedEndTime, icon: null };
    }
    if (status === 'upcoming') {
      if (!formattedStartTime) return null;
      return { label: t('feed.status.opensOn', { on: formattedStartTime }), icon: null };
    }
    if (!endTime) return null;
    const daysLeft = differenceInDays(new Date(endTime), new Date(now));
    if (daysLeft >= 0 && daysLeft <= CLOSING_SOON_DAYS) {
      return { label: t('feed.daysLeft', { days: daysLeft }), icon: 'closingSoon' };
    }
    if (daysLeft > CLOSE_DATE_VISIBLE_DAYS) return null;
    if (!formattedEndTime) return null;
    return { label: t('feed.status.closesOn', { on: formattedEndTime }), icon: 'countDown' };
  };

  const content = getChipContent();
  if (!content) return null;

  return (
    <span className={content.icon ? styles.activityDateChip : styles.activityDateText}>
      {content.icon && <ActivityStateIcon variant={content.icon} />}
      <span>{content.label}</span>
    </span>
  );
}
